'use client';
import React, { useState } from 'react';
import { Clock, ChevronRight, CheckCircle, XCircle, BookOpen, Filter } from 'lucide-react';
import type { Exercise, Difficulty, ExerciseStatus } from './practiceData';

const DIFFICULTY_CONFIG: Record<Difficulty, { label: string; bg: string; text: string; border: string }> = {
  beginner: { label: 'Beginner', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  intermediate: { label: 'Intermediate', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  advanced: { label: 'Advanced', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
};

const STATUS_CONFIG: Record<ExerciseStatus, { label: string; icon: React.ReactNode; color: string }> = {
  'not-started': { label: 'Not Started', icon: null, color: 'text-slate-400' },
  'in-progress': { label: 'In Progress', icon: <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />, color: 'text-amber-600' },
  completed: { label: 'Completed', icon: <CheckCircle size={13} className="text-green-500" />, color: 'text-green-600' },
  failed: { label: 'Needs Review', icon: <XCircle size={13} className="text-red-500" />, color: 'text-red-600' },
};

const EXERCISE_EMOJIS: Record<string, string> = {
  'ex-one-way-lighting': '💡',
  'ex-two-way-lighting': '🔀',
  'ex-radial-socket': '🔌',
  'ex-ring-final': '⭕',
};

interface Props {
  exercises: Exercise[];
  onSelect: (id: string) => void;
}

export default function ExerciseGrid({ exercises, onSelect }: Props) {
  const [filter, setFilter] = useState<Difficulty | 'all'>('all');

  const filtered = filter === 'all' ? exercises : exercises.filter((e) => e.difficulty === filter);
  const completed = exercises.filter((e) => e.status === 'completed').length;

  return (
    <div className="flex-1 overflow-y-auto w-full">
      <div className="max-w-screen-xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground mb-1">Practice Exercises</h1>
            <p className="text-sm text-muted-foreground">
              Work through guided UK domestic wiring exercises. Each exercise checks your wiring against real BS 7671 rules.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-white border border-border rounded-xl px-4 py-2.5 text-center shadow-card">
              <div className="text-xl font-bold text-primary tabular-nums">{completed}/{exercises.length}</div>
              <div className="text-xs text-muted-foreground">Completed</div>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="bg-white border border-border rounded-xl p-4 mb-6 shadow-card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-foreground">Overall Progress</span>
            <span className="text-sm font-bold text-primary tabular-nums">{Math.round((completed / exercises.length) * 100)}%</span>
          </div>
          <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${(completed / exercises.length) * 100}%` }}
            />
          </div>
          <div className="flex gap-4 mt-3">
            {(['not-started', 'in-progress', 'completed', 'failed'] as ExerciseStatus[]).map((s) => {
              const count = exercises.filter((e) => e.status === s).length;
              const cfg = STATUS_CONFIG[s];
              return (
                <div key={`prog-${s}`} className="flex items-center gap-1.5 text-xs">
                  {cfg.icon}
                  <span className={cfg.color}>{count} {cfg.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2 mb-4">
          <Filter size={14} className="text-muted-foreground" />
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mr-1">Filter:</span>
          {(['all', 'beginner', 'intermediate', 'advanced'] as const).map((d) => (
            <button
              key={`filter-${d}`}
              onClick={() => setFilter(d)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all duration-150 capitalize
                ${filter === d
                  ? 'bg-primary text-white border-primary' :'bg-white border-border text-muted-foreground hover:bg-muted'
                }`}
            >
              {d === 'all' ? 'All Levels' : DIFFICULTY_CONFIG[d].label}
            </button>
          ))}
        </div>

        {/* Exercise cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-2 gap-5">
          {filtered.map((exercise) => {
            const diffCfg = DIFFICULTY_CONFIG[exercise.difficulty];
            const statusCfg = STATUS_CONFIG[exercise.status];
            const emoji = EXERCISE_EMOJIS[exercise.id] ?? '🔧';

            return (
              <div
                key={exercise.id}
                className={`bg-white border rounded-xl p-5 shadow-card hover:shadow-elevated transition-all duration-200 cursor-pointer group
                  ${exercise.status === 'completed' ? 'border-green-200' : exercise.status === 'failed' ? 'border-red-100' : 'border-border hover:border-primary/30'}`}
                onClick={() => onSelect(exercise.id)}
              >
                <div className="flex items-start gap-4">
                  <div className="text-3xl flex-shrink-0 w-12 h-12 flex items-center justify-center bg-slate-50 rounded-xl border border-border">
                    {emoji}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="text-base font-bold text-foreground group-hover:text-primary transition-colors duration-150 leading-tight">
                        {exercise.title}
                      </h3>
                      <ChevronRight size={16} className="text-muted-foreground flex-shrink-0 mt-0.5 group-hover:translate-x-0.5 transition-transform duration-150" />
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">{exercise.subtitle}</p>
                    <p className="text-sm text-slate-600 leading-relaxed line-clamp-2 mb-3">{exercise.description}</p>

                    <div className="flex flex-wrap items-center gap-2">
                      {/* Difficulty badge */}
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${diffCfg.bg} ${diffCfg.text} ${diffCfg.border}`}>
                        {diffCfg.label}
                      </span>

                      {/* Status */}
                      <span className={`flex items-center gap-1 text-xs font-medium ${statusCfg.color}`}>
                        {statusCfg.icon}
                        {statusCfg.label}
                      </span>

                      {/* Time */}
                      <span className="flex items-center gap-1 text-xs text-muted-foreground ml-auto">
                        <Clock size={11} />
                        ~{exercise.estimatedTime} min
                      </span>
                    </div>

                    {/* Component count */}
                    <div className="mt-3 pt-3 border-t border-border flex items-center gap-2 flex-wrap">
                      <BookOpen size={11} className="text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">
                        {exercise.requiredComponents.length} component types · {exercise.steps.length} steps
                      </span>
                      <div className="flex gap-1 ml-auto flex-wrap">
                        {exercise.tags.slice(0, 3).map((tag) => (
                          <span key={`tag-${exercise.id}-${tag}`} className="px-1.5 py-0.5 bg-slate-100 text-slate-500 text-xs rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16">
            <div className="text-4xl mb-3">📋</div>
            <p className="text-base font-semibold text-slate-400 mb-1">No exercises for this level yet</p>
            <p className="text-sm text-slate-300">Try a different difficulty filter</p>
          </div>
        )}
      </div>
    </div>
  );
}